<template>
  <component
    v-if="computedProducts"
    :is="computedComponent"
    :products="computedProducts"
    :title="computedTitle(data)"
    :title-analytics="data.title_uk"
    :link="data.link"
    :link-title="data.link_title"
    :show-filter="data.show_filter === '1'"
    :filter-data="data.filter_data"
    :id="data.id"
    :data-for-fixed-block="computedDataForFixed"
    :is-server-pagination="isServerPagination"
    :without-container="withoutContainer"
    :is-cabinet="isCabinet"
    :esputnik-block-id="esputnikBlockId"
    v-bind="isProductPageUnderPrice ? {
      'slider-breakpoints': customSliderBreakpoints,
    } : isCartProductSlider ? {
      'slider-breakpoints': customCartSliderBreakpoints,
      'simple-product-card': true,
      'not-adaptive': false
    } : {}"
  ></component>
</template>

<script>
import { PRODUCTS_BLOCKS_TYPES, ESPUTNIK_BLOCK_CONTEXT } from '@/constants/blocks'
import api from '@/api'
import { EsputnikService } from '@/services/AnalyticService'
import { mapGetters, mapState } from 'vuex'
import ProductsSlider from './ProductsSlider'
import ProductsByCategories from './ProductsByCategories'
import ProductsWithShowMoreBtn from './ProductsWithShowMoreBtn'
import ProductsInfiniteScroll from './ProductsInfiniteScroll'
import ProductsSimpleList from '@/components/product/product-block/ProductsSimpleList'
import * as Sentry from '@sentry/vue'

export default {
  name: 'ProductsBlock',
  props: {
    data: {
      type: Object,
      required: true
    },
    productSku: [String, Number],
    categoryKey: [String, Number],
    isProductPageUnderPrice: {
      type: Boolean,
      default: false
    },
    withoutContainer: {
      type: Boolean,
      default: false
    },
    isCabinet: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loadedProducts: null,
      isAdaptiveMode: !window.matchMedia(`(min-width: 1024px)`).matches,
      esputnikBlockId: ''
    }
  },
  components: {
    ProductsWithShowMoreBtn,
    ProductsByCategories,
    ProductsSlider
  },
  computed: {
    ...mapGetters(['urlLanguagePrefix']),
    ...mapState({
      cartProducts: state => state.cart.products,
      productInfo: state => state.productInfo.productInfo
    }),
    productRootSku () {
      const hasIsMain = this.productInfo.analyticsData.find(el => el.is_main_product)
      if (hasIsMain) {
        return hasIsMain.id
      }
      return this.productInfo.analyticsData[0].id
    },
    computedProducts () {
      if (this.data.type === PRODUCTS_BLOCKS_TYPES.ESPUTNIK_RECOMENDATION) return this.loadedProducts
      return this.data.data
    },
    isCartProductSlider () {
      return this.data.blocks_placement_uid === 'cart'
    },
    computedComponent () {
      if (this.isProductPageUnderPrice) return ProductsSlider
      if (this.isCabinet) return ProductsSimpleList
      if (this.isCartProductSlider) return ProductsSlider
      if (this.data.type === 'esputnik_recommendation' || this.data.type === 'viewed_products') return ProductsSlider

      let component = ProductsWithShowMoreBtn

      switch (this.data.template) {
        case 'slider':
          component = ProductsSlider
          break
        case 'show_more':
          component = ProductsWithShowMoreBtn
          break
        case 'infinite_scroll':
          component = ProductsInfiniteScroll
          break
        case 'category_left':
          component = ProductsByCategories
          break
      }

      return component
    },
    computedDataForFixed () {
      if (this.data.type === PRODUCTS_BLOCKS_TYPES.FIXED) {
        return {
          'goods_all': this.data.goods_all,
          'goods_left': this.data.goods_left,
          'offset': this.data.offset
        }
      }

      return null
    },
    isServerPagination () {
      return this.data.type === PRODUCTS_BLOCKS_TYPES.FIXED
    },
    customCartSliderBreakpoints () {
      return {
        319: {
          slidesPerView: 1.2
        },
        550: {
          slidesPerView: 2.2
        },
        769: {
          slidesPerView: 3
        }
      }
    },
    customSliderBreakpoints () {
      if (this.isProductPageUnderPrice) {
        return {
          319: {
            slidesPerView: 1.2
          },
          550: {
            slidesPerView: 2.2
          },
          769: {
            slidesPerView: 1.5
          },
          1024: {
            slidesPerView: 2
          },
          1400: {
            slidesPerView: 3
          }
        }
      }

      return null
    }
  },
  methods: {
    computedTitle (item) {
      return item.show_title === 'Y' ? item.title : ''
    }
  },
  created () {
    const { settings, type, show_out_of_stock, drop_out_of_stock } = this.data

    if (type === PRODUCTS_BLOCKS_TYPES.ESPUTNIK_RECOMENDATION) {
      const { variant_id: variantId, context } = settings

      const params = {
        variantId
      }

      if (context === ESPUTNIK_BLOCK_CONTEXT.CONTEXT_CART && this.cartProducts) {
        params.productIds = this.cartProducts.map((item) => {
          const isMainProduct = item.analytics_data.find(el => el.is_main_product)
          if (isMainProduct) {
            return isMainProduct.id
          } else if (item.analytics_data.length) {
            return item.analytics_data[0].id
          } else {
            return item.code
          }
        })
      }

      if (context === ESPUTNIK_BLOCK_CONTEXT.CONTEXT_PRODUCT && (this.productRootSku || this.productSku)) {
        params.productId = this.productRootSku || this.productSku
      }

      if (context === ESPUTNIK_BLOCK_CONTEXT.CONTEXT_CATEGORY && this.categoryKey) {
        params.categoryKey = this.categoryKey
      }

      EsputnikService.getRecommendations(params).then(products => {
        if (products?.length) {
          EsputnikService.sendProductImpressions({ products: products })

          const skus = products?.map(item => item.id) ?? []
          const esputnikBlockId = products[0].container_type
          this.esputnikBlockId = esputnikBlockId

          if (skus.length) {
            api.blocks.getEsputnikRecommendations({
              skus,
              show_out_of_stock,
              drop_out_of_stock
            })
              .then(res => {
                if (res?.data?.length) {
                  this.loadedProducts = res.data
                    .map(item => {
                      return {
                        ...item,
                        link: item.link + '?sc_content=' + esputnikBlockId
                      }
                    })
                }
              })
          }
        }
      }).catch(err => {
        Sentry.captureMessage('Empty esputnik recommendation', {
          extra: { variantId: settings.variant_id, errorMsg: err },
          tags: { category: 'esputnik_recommendation' },
          level: 'warning'
        })
      })
    }
  }
}
</script>

<style scoped>

</style>
